import { AnalyticsSdk, generateUUID, getTimeZone, getSessionID, fetchLocusData, buildSearchContext } from '../pdtUtils';
import {
  LOB_RAILS,
  EVENT_NAME_PAGE_RENDERED,
  EVENT_TYPE_PAGE,
  STORE_ID_MMT,
  ORGANISATION_MMT,
  BU_GROUND_TRANSPORT,
  TRAFFIC_SOURCE_ORGANIC,
  PDT_SCHEMA_ID,
  PDT_SCHEMA_NAME,
  PAGE_TYPE_TEMPLATE,
  PAGE_NAME_SEO,
  FUNNEL_STEP_DETAIL,
  FUNNEL_SOURCE_SEO,
  SEO_PAGE_EVENT,
  XDM_PAGE_NAME
} from '../constants';

interface RouteStop {
  stationCode?: string;
  stationName?: string;
  arrivalTime?: string;
  departureTime?: string;
}

interface TrainPageData {
  sourceStation?: string;
  sourceStationCode?: string;
  destinationStation?: string;
  destinationStationCode?: string;
  trainNumber?: string;
  trainName?: string;
  duration?: string;
  avlClasses?: string[];
  primaryTrainRoute?: RouteStop[];
}

function parseDurationToMinutes(duration?: string): number | null {
  if (!duration) return null;
  const hourMatch = duration.match(/(\d+)\s*hr/i);
  const minMatch = duration.match(/(\d+)\s*min/i);
  return (hourMatch ? parseInt(hourMatch[1], 10) : 0) * 60 + (minMatch ? parseInt(minMatch[1], 10) : 0);
}

export const logTrainPageLoadEvent = async (trainData?: TrainPageData) => {
  try {
    if (typeof window === 'undefined' || !AnalyticsSdk || !trainData) return;

    const isDev = process.env.NODE_ENV !== 'production';
    const sessionId = getSessionID();
    const journeyId = generateUUID();
    const locusData = await fetchLocusData(trainData?.sourceStationCode, trainData?.destinationStationCode);

    const searchContext: Record<string, any> = buildSearchContext({
      fromCode: trainData?.sourceStationCode,
      fromName: trainData?.sourceStation,
      toCode: trainData?.destinationStationCode,
      toName: trainData?.destinationStation,
      locusData
    });

    if (trainData?.primaryTrainRoute?.length) {
      searchContext.stops = trainData.primaryTrainRoute.map((stop, index) => ({
        index,
        locus: { locus_id: 'NA', locus_type: 'NA', country: 'India', locus_v2_id: 'NA', locus_v2_type: 'NA' },
        location: {
          code: stop?.stationCode || 'NA',
          name: stop?.stationName || 'NA',
          country_code: 'IN',
          country: 'India'
        }
      }));
    }

    const components = {
      id: 'train_page_load',
      component_type: 'train_detail',
      product_list: [
        {
          id: trainData?.trainNumber || 'NA',
          train_number: parseInt(trainData?.trainNumber || '0', 10) || 0,
          train_name: trainData?.trainName || 'NA',
          duration: parseDurationToMinutes(trainData?.duration),
          position: { v: 1 },
          from_station: { code: trainData?.sourceStationCode || 'NA', name: trainData?.sourceStation || 'NA' },
          to_station: { code: trainData?.destinationStationCode || 'NA', name: trainData?.destinationStation || 'NA' },
          product_type: 'train',
          class: trainData?.avlClasses?.join(',') || null
        }
      ],
      content_details: [
        {
          id: 'train_route',
          type: 'train',
          position: { v: 1 },
          from: {
            location: {
              code: trainData?.sourceStationCode || 'NA',
              name: trainData?.sourceStation || 'NA',
              country_code: 'IN',
              country: 'India'
            },
            locus: {
              locus_id: locusData?.fromLocusCode || 'NA',
              locus_type: locusData?.sourceLocusType || 'NA',
              country: 'India',
              locus_v2_id: locusData?.sourceLocusV2Id || 'NA',
              locus_v2_type: locusData?.sourceLocusV2Type || 'NA'
            }
          },
          to: {
            location: {
              code: trainData?.destinationStationCode || 'NA',
              name: trainData?.destinationStation || 'NA',
              country_code: 'IN',
              country: 'India'
            },
            locus: {
              locus_id: locusData?.toLocusCode || 'NA',
              locus_type: locusData?.destinationLocusType || 'NA',
              country: 'India',
              locus_v2_id: locusData?.destinationLocusV2Id || 'NA',
              locus_v2_type: locusData?.destinationLocusV2Type || 'NA'
            }
          }
        }
      ]
    };

    const payload = {
      page_context: {
        lob: LOB_RAILS,
        page_type: PAGE_TYPE_TEMPLATE,
        page_name: XDM_PAGE_NAME.TRAIN,
        prev_page_name: PAGE_NAME_SEO,
        funnel_step: FUNNEL_STEP_DETAIL,
        lob_category: LOB_RAILS,
        travel_store: { store_id: STORE_ID_MMT }
      },
      event_tracking_context: {
        env: isDev ? 'dev' : 'prod',
        timezone: getTimeZone(),
        session_id: sessionId,
        funnel_source: FUNNEL_SOURCE_SEO,
        bu: BU_GROUND_TRANSPORT,
        organisation: ORGANISATION_MMT,
        journey_id: journeyId,
        traffic_source: TRAFFIC_SOURCE_ORGANIC,
        template_id: PDT_SCHEMA_ID,
        topic_name: PDT_SCHEMA_NAME
      },
      search_context: searchContext,
      event_detail: {
        event_name: EVENT_NAME_PAGE_RENDERED,
        event_type: EVENT_TYPE_PAGE,
        event_id: generateUUID(),
        event_timestamp: Date.now(),
        event_value: SEO_PAGE_EVENT.TRAIN,
        components
      },
      experiment_details: {
        valid_exp_list: [] as string[],
        variant_keys: '',
        honoured_exp_list: [] as string[]
      }
    };

    const xdmPayload = {
      ...payload,
      event_detail: {
        ...payload.event_detail,
        components: [components]
      }
    };

    const extraOptions = { lob: LOB_RAILS, destinationUrl: window?.location?.href || '' };

    AnalyticsSdk?.pushToPDT?.(payload, extraOptions);
    AnalyticsSdk?.pushToAdobeDataLayer?.(xdmPayload, extraOptions);
  } catch (e) {
    console.error('Analytics: train page load event failed', e);
  }
};
